import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { ModalBannersTabs } from "configs/types";
import { closeModal } from "store/modals/slice";
import {
  handleClearFields,
  handleUpdateFieldsGroup,
  handleUpdateFields,
  getBannerGroupItem,
  createBanner,
  createBannerGroup,
  updateBanner,
  updateBannerGroup,
  deleteBanner,
  BannerListItem,
} from "store/cms/banners/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";

import Button from "components/buttons/Button";
import { Modal } from "components/Modal/Modal";
import styles from "./CmsBannersEditModal.module.scss";

import cn from "clsx";
import { selectFilteredGeo } from "store/geoFilter/selectors";
import BannersTrailTab from "./tabs/BannersTrailTab";
import BannersMainTab from "./tabs/BannersMainTab";
import BannersBannersTab from "./tabs/BannersBannersTab";
import { useFormErrors } from "helpers/useFormErrors";
import ModalSkeleton from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import { handleApiError } from "helpers/handleApiError";
import { usePermissionHelper } from "helpers/usePermissionHelper";

const REQUIRED_FIELDS = {
  title: false,
  cta: false,
};

const IMAGE_SIZE: Record<string, string> = {
  1: "1310 × 250",
  2: "647 × 250",
  3: "425 × 250",
};
const CmsBannersEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const inputRef = useRef<HTMLInputElement>(null);
  const inputRef2 = useRef<HTMLInputElement>(null);
  const formErrors = useFormErrors();

  const localesList = useAppSelector((store) => store.settings.locales);
  const geoList = useAppSelector(selectFilteredGeo);
  const { isItemFetched, isFetching } = useAppSelector(
    (store) => store["cms-banners"],
  );
  const payloadGroup = useAppSelector(
    (store) => store["cms-banners"].payloadGroup,
  );
  const [activeLocale, setActiveLocale] = useState<string>();
  const [activeItem, setActiveItem] = useState<string>("Item1");
  const [activeGeo, setActiveGeo] = useState<string>();
  const [geoBannersList, setGeoBannersList] = useState<BannerListItem[]>();
  const payload = useAppSelector(
    (store) =>
      store["cms-banners"].payload?.filter(
        (el) => el.innerId === activeItem && el.geo === activeGeo,
      )[0],
  );
  const payloadList = useAppSelector((store) => store["cms-banners"].payload);
  const payloadLocale = payload?.locales?.filter(
    (el) => el.locale === activeLocale,
  )[0];

  const [tabs] = useState<ModalBannersTabs[]>([
    ModalBannersTabs.main,
    ModalBannersTabs.banners,
    ModalBannersTabs.trail,
    // ModalBannersTabs.settings,
  ]);
  const [activeTab, setActiveTab] = useState<ModalBannersTabs>(
    ModalBannersTabs.main,
  );
  const [hasMainData, setHasMainData] = useState<boolean>(false);
  // const [deleteValue, setDeleteValue] = useState<string>("");
  const [imgPreview, setImgPreview] = useState<string>("");
  const [imgPreviewBig, setImgPreviewBig] = useState<string>("");

  const [errorfields, setErrorfields] = useState(() => REQUIRED_FIELDS);
  const isEditing = Boolean(searchParams.get("entityId"));
  const entityId = searchParams.get("entityId");

  const permissions = usePermissionHelper();

  const hasSelectedGeo = !isEditing
    ? Boolean(payloadGroup?.geo?.length)
    : isItemFetched && Boolean(payloadGroup?.geo?.length);

  useEffect(() => {
    if (localesList?.length > 0 && activeItem) {
      setActiveLocale(localesList[0]);

      if (inputRef.current) {
        inputRef.current.value = "";
      }
      if (inputRef2.current) {
        inputRef2.current.value = "";
      }

      if (!payloadLocale) {
        dispatch(
          handleUpdateFields({
            locales: [
              // @ts-ignore
              ...(payload?.locales ?? []),
              // @ts-ignore
              { ...(payloadLocale ?? {}), locale: localesList[0] },
            ],
            innerId: activeItem,
            geo: activeGeo ?? "",
          }),
        );
      }
    }
  }, [localesList, activeItem, dispatch]);

  useEffect(() => {
    if (payloadGroup && payloadGroup.banners) {
      setGeoBannersList(
        payloadGroup.banners.filter((item) => item.geo === activeGeo),
      );
    }
  }, [activeGeo, payloadGroup]);

  useEffect(() => {
    setImgPreview("");
    setImgPreviewBig("");
  }, [activeItem, activeGeo]);

  useEffect(() => {
    if (!payload) return;

    if (payload.imageSmall) {
      if (typeof payload.imageSmall === "string") {
        setImgPreview(payload.imageSmall);
      } else {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImgPreview(reader.result as string);
        };
        reader.readAsDataURL(payload.imageSmall);
      }
    }

    if (payload.imageBig) {
      if (typeof payload.imageBig === "string") {
        setImgPreviewBig(payload.imageBig);
      } else {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImgPreviewBig(reader.result as string);
        };
        reader.readAsDataURL(payload.imageBig);
      }
    }
  }, [payload]);

  useEffect(() => {
    if (entityId !== null) {
      dispatch(getBannerGroupItem(entityId));
      setHasMainData(true);
    }
  }, [dispatch, entityId]);

  useEffect(() => {
    setErrorfields(REQUIRED_FIELDS);
  }, [activeLocale, activeItem, activeGeo]);

  const isLoaded = () => {
    if (
      entityId !== null &&
      (!isItemFetched || String(payloadGroup?.id) !== entityId)
    ) {
      return false;
    }

    return true;
  };

  const handleTabClick = (e: ModalBannersTabs) => {
    setActiveTab(e);
  };
  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearFields());
  };

  const handleDataGroup = (
    key: string,
    value: string | boolean | number,
    img?: File | null,
  ) => {
    // @ts-ignore
    dispatch(handleUpdateFieldsGroup({ [key]: img ?? value }));
  };
  const handleData = (
    key: string,
    value: string | boolean | number,
    img?: File | null,
  ) => {
    console.log("handleData", key, value);
    dispatch(
      // @ts-ignore
      handleUpdateFields({
        [key]: img ?? value,
        innerId: activeItem,
        geo: activeGeo ?? "",
        bannerGroupId: payloadGroup?.id?.toString() ?? "",
      }),
    );
  };
  const handleDataLocale = (
    key: string,
    value: string | boolean | number,
    innerId: string,
  ) => {
    setErrorfields(REQUIRED_FIELDS);
    dispatch(
      // @ts-ignore
      handleUpdateFields({
        locales: [
          // @ts-ignore
          ...(payload?.locales
            ? payload.locales.filter((el) => el.locale !== activeLocale)
            : []),
          {
            // @ts-ignore
            locale: activeLocale,
            ...(payloadLocale ?? {}),
            [key]: value,
          },
        ],
        innerId: innerId,
        geo: activeGeo ?? "",
        bannerGroupId: payloadGroup?.id?.toString() ?? "",
      }),
    );
  };

  const handleDeleteClickBanner = async () => {
    const result = await dispatch(deleteBanner(payload?.id?.toString() ?? "0"));
    if (result.payload === true) {
      setImgPreview("");
      setImgPreviewBig("");
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
    dispatch(getBannerGroupItem(searchParams.get("entityId") ?? ""));
  };

  const renderTab = () => {
    if (activeTab === ModalBannersTabs.banners) {
      return (
        <BannersBannersTab
          t={t}
          activeItem={activeItem}
          activeLocale={activeLocale}
          payload={payload}
          payloadList={payloadList}
          payloadLocale={payloadLocale}
          geoBannersList={geoBannersList}
          localesList={localesList}
          errorfields={errorfields}
          imgPreview={imgPreview}
          imgPreviewBig={imgPreviewBig}
          inputRef={inputRef}
          inputRef2={inputRef2}
          setActiveItem={setActiveItem}
          setActiveLocale={setActiveLocale}
          handleData={handleData}
          handleDataLocale={handleDataLocale}
          IMAGE_SIZE={IMAGE_SIZE}
          clearFieldError={formErrors.clearFieldError}
          fieldErrors={formErrors.errors}
          readOnly={!permissions?.update}
        />
      );
    }
    if (activeTab === ModalBannersTabs.main) {
      return (
        <BannersMainTab
          payloadGroup={payloadGroup}
          onChange={handleDataGroup}
          t={t}
          readOnly={!permissions?.update}
        />
      );
    }

    if (activeTab === ModalBannersTabs.trail) {
      const entityId = payloadGroup?.id;
      if (!entityId) return null;

      return <BannersTrailTab entityId={entityId} />;
    }
  };

  const handleApply = async () => {
    if (payloadGroup !== undefined && activeTab === ModalBannersTabs.main) {
      try {
        const result = await dispatch(
          hasMainData
            ? updateBannerGroup(payloadGroup)
            : createBannerGroup(payloadGroup),
        ).unwrap();

        dispatch(
          showNotification({
            title: t("success"),
            message: hasMainData
              ? t("entityUpdateSuccess")
              : t("entityCreateSuccess"),
            type: NotificationTypes.success,
          }),
        );

        if (!hasMainData) {
          navigate(
            `${location.pathname}?activeModal=${ModalTypes.banners}&entityId=${result}`,
          );
          dispatch(openModal({ activeModal: ModalTypes.banners }));
        }
      } catch (error) {
        handleApiError({
          error,
          t,
          dispatch,
          formErrors,
        });
      }
    }

    if (payload !== undefined && activeTab === ModalBannersTabs.banners) {
      let groupId = payloadGroup?.id ? payloadGroup.id : "";
      const data = { ...payload, bannerGroupId: groupId };

      try {
        await dispatch(
          payload.id ? updateBanner(data) : createBanner(payload),
        ).unwrap();

        dispatch(getBannerGroupItem(searchParams.get("entityId") ?? ""));

        dispatch(
          showNotification({
            title: t("success"),
            message: payload.id
              ? t("entityUpdateSuccess")
              : t("entityCreateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } catch (error) {
        handleApiError({
          error,
          t,
          dispatch,
          formErrors,
        });
      }
    }
  };

  const validationFields = (payload: any) => {
    if (payload && payload.locales) {
      let currentItemFields = payload.locales.find(
        (item: { locale?: string }) => item.locale === activeLocale,
      );

      if (payload.anchor) {
        currentItemFields = { ...currentItemFields };
      }

      let hasError = false;
      Object.keys(REQUIRED_FIELDS).forEach((field) => {
        if (!currentItemFields?.[field]) {
          hasError = true;
          setErrorfields((prev) => ({
            ...prev,
            [field]: true,
          }));
        }
      });

      return hasError;
    } else {
      setErrorfields(
        Object.keys(REQUIRED_FIELDS).reduce(
          (acc, field) => {
            return {
              ...acc,
              [field]: true,
            };
          },
          {} as { title: boolean; cta: boolean },
        ),
      );
      return true;
    }
  };

  const isBtnDisabled = () => {
    if (
      activeTab === ModalBannersTabs.main &&
      (!payloadGroup?.geo || !payloadGroup?.title)
    ) {
      return true;
    }
    return false;
  };

  

  return (
    <Modal
      size="sm"
      onClose={handleCloseClick}
      className={styles.countriesModal}
    >
      <Modal.Header>{t("banners")}</Modal.Header>
      {!isLoaded() ? (
        <Modal.Content className={styles.cmsBannersContentWrapper}>
          <ModalSkeleton />
        </Modal.Content>
      ) : (
        <>
          <Modal.Tabs
            tabs={tabs}
            activeTab={activeTab}
            canBeClicked={hasMainData}
            disabledTabs={hasSelectedGeo ? [] : [ModalBannersTabs.banners]}
            onClick={(tab) => {
              if (tab === ModalBannersTabs.banners) {
                if (!payloadGroup?.geo || payloadGroup.geo.length === 0) {
                  dispatch(
                    showNotification({
                      title: t("warning"),
                      message: t("selectGeoFirst"),
                      type: NotificationTypes.warning,
                    }),
                  );
                  return;
                }

                if (!payloadGroup?.id) {
                  dispatch(
                    showNotification({
                      title: t("warning"),
                      message: t("saveMainDataFirst"),
                      type: NotificationTypes.warning,
                    }),
                  );
                  return;
                }
              }

              handleTabClick(tab);

              if (tab === ModalBannersTabs.banners) {
                setActiveGeo(payloadGroup?.geo?.[0]);
              }
            }}
          />

          <Modal.Content
            className={cn(styles.cmsBannersContentWrapper, {
              [styles.trailContentWrapper]: activeTab === ModalBannersTabs.trail,
            })}
          >
            {(activeTab === ModalBannersTabs.main ||
              activeTab === ModalBannersTabs.banners) && (
              <div className={styles.tabsGeo}>
                {activeTab === ModalBannersTabs.main && (
                  <Modal.Tabs
                    tabs={geoList}
                    activeTab={payloadGroup?.geo}
                    onClick={(t) => {
                      dispatch(
                        // @ts-ignore
                        handleUpdateFieldsGroup({
                          ...payloadGroup,
                          geo: payloadGroup?.geo?.includes(t)
                            ? payloadGroup?.geo?.filter((item) => item !== t)
                            : [...(payloadGroup?.geo ?? []), t],
                        }),
                      );
                    }}
                    isGeoTabs={true}
                    isSmallModifier={true}
                  />
                )}

                {activeTab === ModalBannersTabs.banners && (
                  <Modal.Tabs
                    tabs={payloadGroup?.geo || []}
                    activeTab={activeGeo}
                    onClick={setActiveGeo}
                    isGeoTabs={true}
                    isSmallModifier={true}
                  />
                )}
              </div>
            )}

            <div className="banners-content">{renderTab()}</div>
          </Modal.Content>

          {permissions?.update && activeTab !== ModalBannersTabs.trail && (
            <Modal.Footer>
            {activeTab === ModalBannersTabs.banners && payload?.id && permissions.delete && (
              <Button
                className={cn("delete-banner")}
                txt={t("delete")}
                handleClick={handleDeleteClickBanner}
                disabled={isFetching}
              />
            )}
            <div className={styles.footerActions}>
              <Button
                isSecondary
                txt={t("cancel")}
                handleClick={handleCloseClick}
              />
              <Button
                isPrimary
                isLoad={isFetching}
                disabled={isBtnDisabled() || isFetching}
                handleClick={handleApply}
                txt={t("save")}
              />
            </div>
            </Modal.Footer>
          )}
        </>
      )}
    </Modal>
  );
};

export default CmsBannersEditModal;
